//!#system	

<template>
	<v-card>
		<v-card-title>
			<span class="headline">Import Mapping</span>
		</v-card-title>
		<v-progress-linear v-if="isLoading" indeterminate color="cyan"></v-progress-linear>
		<v-card-text>
			<v-form ref="configform" v-model="formValid" lazy-validation>
				<v-expansion-panels
					v-model="panelsOpen"
					popout
					focusable
				>
					<v-expansion-panel>
						<v-expansion-panel-header>
							File Details
						</v-expansion-panel-header>
						<v-expansion-panel-content>
							<v-select
								v-model="model.sourceFileType"
								:items="sourceFileTypes"
								label="Source file type"
							/>
							<div v-if="isDelimitedType">
								<v-combobox
									class="mb-6"
									v-model="model.delimiter"
									:items="delimiters"
									label="Columns separated by"
									hint="Don't see your option?  Type the character for the delimiter."
									:persistent-hint="true"
								/>
								<v-combobox
									class="mb-6"
									v-model="model.enclosedBy"
									:items="enclosedBys"
									label="Fields enclosed by"
									hint="Don't see your option?  Type the character for the field enclosure."
									:persistent-hint="true"
									:rules="[v => !(model.enclosedBy && model.enclosedBy.length > 1)]"
								/>
								<v-combobox
									class="mb-6"
									v-model="model.lineEnding"
									:items="lineEndings"
									label="Line Endings"
									hint="Don't see your option?  Type the character for the line ending."
									:persistent-hint="true"
								/>
								<v-switch
									v-model="model.updateOnDuplicate"
									label="Update on duplicates"
									inset
								/>
								<v-switch
									v-model="model.hasHeader"
									label="File has a header and should be ignored"
									inset
								/>
							</div>
						</v-expansion-panel-content>
					</v-expansion-panel>
					<v-expansion-panel v-for="(recordType, recordTypeIndex) in model.recordTypes" :key="recordType.key">
						<v-expansion-panel-header v-if="model.sourceFileType === 'Record-Identifiers'">
							Record Identifier: {{recordType.recordIdentifier || '(empty)'}}
						</v-expansion-panel-header>
						<v-expansion-panel-header v-else-if="model.sourceFileType === 'Single-Row'">
							Single Row Data Mapping
						</v-expansion-panel-header>

						<v-expansion-panel-content>
							<v-btn 
								v-if="model.recordTypes.length > 1"
								class="float-right mt-1 mr-n3" 
								@click="removeRecordType(recordTypeIndex)"
								fab
								dark 
								small
							>
								<v-icon dark small>mdi-trash-can-outline</v-icon>
							</v-btn>
							<v-text-field
							 	v-if="model.sourceFileType === 'Record-Identifiers'"
								v-model="recordType.recordIdentifier"
								label="Record Identifier"
								hint="The first column in the file identifies the record"
								:rules="[v => !!recordType.recordIdentifier]"
							/>
							<v-autocomplete
								v-model="recordType.bucket"
								label="Bucket"
								:items="getBuckets()"
								:rules="[v => !!recordType.bucket]"
							/>
							<v-select
								v-model="recordType.dataLocation"
								label="Data Mapping Location"
								:items="getMapTos(recordType.bucket)"
								:rules="[v => !(!recordType.dataLocation || !getMapTos(recordType.bucket).includes(recordType.dataLocation))]"
							/>
							<fieldset class="field-fs" v-for="(fieldMapping, index) in recordType.fieldMappings" :key="fieldMapping.key">
								<legend>Column: {{fieldMapping.column}} (The {{ordinalSuffix(+fieldMapping.column + extraIdentifierCol)}} column)</legend>
								<v-btn 
									v-if="recordType.fieldMappings.length > 1"
									class="float-right mt-n8" 
									@click="removeField(recordType, index)"
									fab
									dark 
									x-small
								>
									<v-icon dark x-small>mdi-trash-can-outline</v-icon>
								</v-btn>
								<v-text-field
									v-model="fieldMapping.column"
									label="Column"
									hint="Zero-based column number where to find this data"
								/>
								<v-select
									v-model="fieldMapping.mapTo"
									:items="getFields(recordType.bucket)"
									label="Map to"
									:rules="[v => !(!fieldMapping.mapTo || !getFields(recordType.bucket).includes(fieldMapping.mapTo))]"
								/>
							</fieldset>
							<v-btn color="primary" small @click="addField(recordType)">Add field mapping</v-btn>
						</v-expansion-panel-content>
					</v-expansion-panel>
				</v-expansion-panels>
			</v-form>
		</v-card-text>
		<v-card-actions>
			<v-spacer></v-spacer>
			<v-btn text @click="closeDialog()">Exit</v-btn>
			<v-btn text @click="addRecordType()" :disabled="!canAddRecordType">Add record type</v-btn>
			<v-btn color="primary" :disabled="!formValid" @click="saveConfig()" :loading="isSaving">{{submitText}}</v-btn>
		</v-card-actions>
	</v-card>
</template>

<script>
import tools from '@/tools';
import apiService from '@/apiService';
import series from 'to-series';
export default {
	name: 'ImportMappingOptions',
	props: {
		routeInfo: {
			type: Object
		},
		detail: { // Optional starter object
			type: Object,
			default: () => {}
		}
	},
	data() {
		return {
			isLoading: false,
			model: tools.isEmpty(this.detail.configData) ? this.defaultStarterObject() : this.detail.configData,
			panelsOpen: 0,
			fieldInfos: {},
			bucketFields: {},
			bucketRelationships: {},
			bucketInfos: [],
			submitText: 'Save config',
			isSaving: false,
			createableBuckets: [this.detail.bucket],
			formValid: true
		}
	},
	computed: {
		sourceFileTypes() {
			return ['Record-Identifiers', 'Single-Row'];
		},
		isDelimitedType() {
			return this.sourceFileTypes.includes(this.model.sourceFileType);
		},
		delimiters() {
			return ['tab', 'comma'];
		},
		enclosedBys() {
			return ['"'];
		},
		lineEndings() {
			return ['Windows', 'Linux'];
		},
		categories() {
			return ['data', 'lookup']
		},
		canAddRecordType() {
			return Object.keys(this.bucketRelationships).length >= 2 && this.model.sourceFileType === 'Record-Identifiers';
		},
		extraIdentifierCol() {
			return this.model.sourceFileType === 'Record-Identifiers' ? 1 : 0;
		}
	},
	methods: {
		closeDialog() {
			this.$emit('close');
		},
		ordinalSuffix(num) {
			return tools.ordinalSuffix(num);
		},
		defaultStarterObject() {
			let recordIdentifier = 'A';
			const [ namespace, pluralName ] = this.detail.bucket.split('/');
			const firstOfBucket = pluralName.substring(0, 1).toUpperCase();
			const currentRecordIdentifiers = [];
			if (this.model && this.model.recordTypes) {
				this.model.recordTypes.forEach(recordType => {
					currentRecordIdentifiers.push(recordType.recordIdentifier);
				});
				
				if (!currentRecordIdentifiers.includes(firstOfBucket)) {
					recordIdentifier = firstOfBucket;
				}
			} else  {
				recordIdentifier = firstOfBucket;
			}
			return {
				sourceFileType: 'Record-Identifiers',
				delimiter: 'tab',
				enclosedBy: '"',
				lineEnding: 'Windows',
				updateOnDuplicate: false,
				hasHeader: false,
				recordTypes: [{
					key: 'rt-' + tools.randomString(4),
					recordIdentifier: recordIdentifier,
					description: '',
					bucket: this.detail.bucket,
					dataLocation: this.detail.bucket,
					fieldMappings: [{
						key: 'fm-' + tools.randomString(4),
						column: 1,
						category: 'data',
						mapTo: ''
					}]
				}]
			};
		},
		addField(recordTypeRef) {
			const lastCol = recordTypeRef.fieldMappings.map(v => v).pop();
			const columnNumber = +(lastCol.column) + 1;
			recordTypeRef.fieldMappings.push({
						key: 'fm-' + tools.randomString(4),
						column: columnNumber,
						category: 'data',
						mapTo: ''
			});
		},
		removeField(recordTypeRef, index) {
			this.$delete(recordTypeRef.fieldMappings, index);
		},
		addRecordType() {
			this.model.recordTypes.push({
				key: 'rt-' + tools.randomString(4),
				recordIdentifier: '',
				description: '',
				bucket: Object.keys(this.fieldInfos).map(v => v).pop(),
				dataLocation: 'root',
				fieldMappings: [{
					key: 'fm-' + tools.randomString(4),
					column: 1,
					category: 'data',
					mapTo: ''
				}]
			});
			this.panelsOpen = this.model.recordTypes.length;
		},
		removeRecordType(index) {
			this.$delete(this.model.recordTypes, index);
		},
		getFieldInfo(options, done) {
			const { bucket, key } = options;
			const [namespace, pluralName] = bucket.split('/');
			if (this.fieldInfos[bucket]) return done(); // we've already loaded this one
			this.$store.dispatch('app/fieldInfo', {
				routeInfo: {namespace, pluralName},
				callback: (err, fieldInfoResponse) => {
					if (err) return done(err);
					this.fieldInfos[bucket] = fieldInfoResponse;
					this.bucketFields[bucket] = this.fields(bucket);
					this.bucketRelationships[bucket] = this.relationships(bucket, key);
					return done(null);
				}
			});
		},
		fields(bucket) {
			if (!this.fieldInfos[bucket]) return [];
			const fiFields = this.fieldInfos[bucket].fields;
			const fields = [];
			fiFields.forEach(field => {
				if (['id', 'createdAt', 'updatedAt'].includes(field.name)) return;
				if (field.name.endsWith('_id')) return;
				if (field.relationship && field.relationship.childCreate === true && field.relationship.joinType === 'junction') return;
				if (!field.createable) return;
				if (field.relationship && field.relationship.childCreate === false) {
					const subBucket = field.parent.namespace + '/' + field.parent.pluralName;
					this.getFieldInfo({ bucket: subBucket }, () => {
						const fi = this.fieldInfos[bucket];
						let refArray = this.bucketFields[subBucket];
						if (refArray.length > 0) {
							fields.push(field.name + ':' + refArray[0]);
						}
					});
				} else if (field.relationship) {
					const subBucket = field.parent.namespace + '/' + field.parent.pluralName;
					this.getFieldInfo({ bucket: subBucket }, () => {
						const fi = this.fieldInfos[bucket];
						let refArray = this.bucketFields[subBucket];
						refArray.forEach(refField => {
							fields.push(field.name + ':' + refField);
						});
					});
				} else {
					fields.push(field.name)
				}
			});
			return fields;
		},
		relationships(bucket, key) {
			if (!this.fieldInfos[bucket]) return [];
			const fiFields = this.fieldInfos[bucket].fields;
			const rels = [];
			fiFields.forEach(field => {
				if (['id', 'createdAt', 'updatedAt'].includes(field.name)) return;
				if (field.name.endsWith('_id')) return;
				//if (field.type.isId) return;
				if (!field.relationship) return;
				if (field.relationship && field.relationship.childCreate === false) return;
				this.createableBuckets.push(field.parent.namespace + '/' + field.parent.pluralName);
				rels.push({
					property: (key ? key + ':' : '') + field.name,
					//property: field.name,
					routeInfo: field.parent,
					bucket: field.parent.namespace + '/' + field.parent.pluralName
				});
			});
			return rels;
		},
		buildBucketInfos(options, done) {
			const { bucket } = options;
			let { key } = options;
			if (!key) key = '';
			this.getFieldInfo({ bucket, key }, err => {
				const rels = this.bucketRelationships[bucket];
				series()
					.each(rels, (rel, cb) => {
						if (rel.property.startsWith(key + ':')) key = '';
						return this.buildBucketInfos({
							bucket: rel.routeInfo.namespace + '/' + rel.routeInfo.pluralName,
							key: (key ? key + ':' : '') + rel.property
						}, cb);
					})
					.end(done);
			});
		},
		getMapTos(bucketd) {
			const rels = [];
			Object.keys(this.bucketRelationships).forEach(bucket => {
				const rl = this.bucketRelationships[bucket];
				rl.forEach(r => {
					if (r.bucket !== bucketd) return;
					rels.push('(' + this.detail.bucket + ')' + ':' + r.property);
				});
			});
			if (!rels.length) rels.splice(0, 0, this.detail.bucket);
			return rels;
		},
		getFields(bucket) {
			return this.bucketFields[bucket];
		},
		getBuckets() {
			return this.createableBuckets;
		},
		getInitialData() {
			this.isLoading = true;
			series()
				.first(cb => {
					return this.buildBucketInfos({ bucket: this.detail.bucket }, cb);
				})
				.end(err => {
					this.isLoading = false;
				});
		},
		validate() {
			this.$refs.configform.validate();
			const recordIdentifiers = [];
			const errs = {
				duplicateRecordIdentifiers: false,
				missingRecordIdentifiers: false
			};
			this.model.recordTypes.forEach(recordType => {
				if (!recordType.recordIdentifier) {
					errs.missingRecordIdentifiers = true;
				}
				if (recordIdentifiers.includes(recordType.recordIdentifier)) {
					errs.duplicateRecordIdentifiers = true;
				}
				recordIdentifiers.push(recordType.recordIdentifier);
			});
			if (errs.duplicateRecordIdentifiers) {
				return 'You have duplicate record identifier values in your config. Each record must start with a unique value to identify what that record is.';
			}
			if (errs.missingRecordIdentifiers) {
				return 'You have missing record identifier values in your config. Each record must start with a unique value to identify what that record is.';
			}
			return null;
		},
		saveConfig(done) {
			const validationResult = this.validate();
			if (validationResult) {
				return this.$store.dispatch('app/applyObjectError', {
					show: true,
					text: validationResult
				});
			}
			this.isSaving = true;
			apiService.update({
				...this.routeInfo,
				body: {
					...this.detail,
					configData: this.model
				}
			}, (err, response, meta) => {
				this.isSaving = false;
				if (err) {
					this.$store.dispatch('app/applyObjectError', {
						show: true,
						text: meta.validation ? meta.validation.messages.join('\n') : err  
					});
				} else {
					this.$store.dispatch('app/applyObjectUpdated', {
						show: true,
						text: 'Your data has been updated.'
					});
					this.$emit('refresh');
				}
			});
		}
	},
	/**
	 * {
	 * 	bucket: 'something/something',
	 * 	path: 'root.items',
	 * 	fields: [{...}]
	 * }
	 */
	created() {
		this.getInitialData();
	}
}
</script>

<style scoped>
.field-fs {
	padding: 20px;
	border: 3px solid #eee;
	position: relative;
	margin-bottom: 20px;
}
.field-fs legend {
	padding: 0 20px 0 20px;
}
</style>